/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */

import { chunk, insertAtIndex, removeAtIndex } from '@openphone/lib/collections'
import isNonNull from '@openphone/lib/isNonNull'
import { isValidNumber, toE164 } from '@openphone/lib/phone-number'
import { startDurationVital, stopDurationVital } from '@openphone/quo-observability'
import DataLoader from 'dataloader'
import { action, makeAutoObservable, observable, remove, runInAction } from 'mobx'
import type { Subscription } from 'rxjs'
import { Subject } from 'rxjs'

import type ById from '@src/lib/ById'
import { DisposeBag } from '@src/lib/DisposeBag/DisposeBag'
import StatefulPromise from '@src/lib/StatefulPromise'
import { logError } from '@src/lib/log'
import omitNullish from '@src/lib/omitNullish'
import PersistedCollection from '@src/service/collections/PersistedCollection'
import type { GroupMembership } from '@src/service/model'
import { buffer, MemberModel } from '@src/service/model'
import type { CodableCsvImportV2 } from '@src/service/model/CsvImportV2Model'
import CsvImportV2Model from '@src/service/model/CsvImportV2Model'
import type { CodableContact } from '@src/service/model/contact/ContactModel'
import { ContactModel } from '@src/service/model/contact/ContactModel'
import { ContactTemplateItemModel } from '@src/service/model/contact/ContactTemplateItemModel'
import type { RawGoogleContactSettings } from '@src/service/model/contact/GoogleContactSettingsModel'
import { GoogleContactSettingsModel } from '@src/service/model/contact/GoogleContactSettingsModel'
import type { NoteModel } from '@src/service/model/contact/NoteModel'
import type { CodableSharedContactSettings } from '@src/service/model/contact/SharedContactSettingsModel'
import { SharedContactSettingsModel } from '@src/service/model/contact/SharedContactSettingsModel'
import type { CodableContactNoteReaction } from '@src/service/model/reactions/ContactNoteReactionModel'
import ContactNoteReactionModel, {
  isCodableContactNoteReaction,
} from '@src/service/model/reactions/ContactNoteReactionModel'
import makePersistable from '@src/service/storage/makePersistable'
import type ContactsClient from '@src/service/transport/contacts'
import type { TransactionConfiguration } from '@src/service/transport/transaction'
import type CsvImportV2Repository from '@src/service/worker/repository/CsvImportV2Repository'
import type { SharedContactSettingsRepository } from '@src/service/worker/repository/SharedContactSettingsRepository'

import type Service from '.'
import type { CollectionChange } from './collections/Collection'
import Collection from './collections/Collection'
import type ConversationParticipantCodable from './model/ConversationParticipantCodable'
import type { ContactItemModel } from './model/contact/ContactItemModel'
import type { GooglePeopleSyncProgressNotification } from './transport/websocket'
import type MainWorker from './worker/main'
import type {
  ContactGoogleSettingsRepository,
  ContactRepository,
  ContactTemplateItemRepository,
} from './worker/repository'

interface CsvImport {
  name: string
  userId: string
}

const CONTACT_BULK_GET_MAX_IDS = 200

export default class ContactStore {
  private readonly disposeBag = new DisposeBag()

  private readonly collection: PersistedCollection<ContactModel, ContactRepository>
  /**
   * In-memory collection of contacts. Contacts are fetched on demand from the
   * GraphQL contacts API and cached here instead of in the persisted collection.
   */
  private readonly inMemoryCollection: Collection<ContactModel>
  readonly template: PersistedCollection<
    ContactTemplateItemModel,
    ContactTemplateItemRepository
  >
  readonly googleContactSettings: PersistedCollection<
    GoogleContactSettingsModel,
    ContactGoogleSettingsRepository
  >
  settings: PersistedCollection<
    SharedContactSettingsModel,
    SharedContactSettingsRepository
  >
  /**
   * @deprecated will be used only for legacy csv imports that were made locally
   */
  csvImports: CsvImport[] = []
  /**
   * Part of the new csv import flow.
   * It will hold all the information about the csv import jobs
   * Includes deleted entries
   */
  csvImportsV2: PersistedCollection<CsvImportV2Model, CsvImportV2Repository>
  /**
   * A collection derived from csvImportsV2.
   * It will hold only the active csv import jobs
   */
  activeCsvImportsV2 = new Collection<CsvImportV2Model>({
    filter: (item) => !item.deletedAt && item.status !== 'incomplete',
  })
  loaded = false
  isFetchingContacts = false

  private readonly byNumber = observable.map<string, Map<string, ContactModel>>()
  private contactNumbers: ById<string[]> = {}
  /**
   * Tracks phone numbers that have already been fetched or are in-flight
   * via fetchContactByNumberFromApi to prevent redundant API calls.
   */
  private fetchedByNumberCache = new Set<string>()
  /**
   * Tracks contact IDs that were recently merged (absorbed into another contact).
   * Prevents re-adding these contacts when the API returns stale data
   * due to eventual consistency. Cleaned up on websocket 'contact-delete'.
   */
  private recentlyMergedIds = new Set<string>()
  private contactByIdLoader = new DataLoader<string, ContactModel | null>(
    async (ids) => {
      // Clear dataloader cache when making a new batched request
      this.contactByIdLoader.clearAll()

      // Make the actual API call to fetch the contacts from the server or local database.
      await this.getByIds([...ids])

      // Return the data in the same order as the ids, if the contact is not found, return null
      return ids.map((id) => this.contactCollection.get(id) ?? null)
    },
    {
      maxBatchSize: CONTACT_BULK_GET_MAX_IDS,

      // Window of 100ms to avoid making too many API calls
      batchScheduleFn: (callback) => setTimeout(callback, 100),
    },
  )

  /**
   * Last contacts resync id received.
   *
   * This acts as a sequential ID that we use to determine if we need to resync
   * contacts from the server.
   */
  private lastContactsResyncId: string | null = null
  private contactUpdate$ = new Subject<ContactModel>()
  private contactTemplateItemUpdate$ = new Subject<ContactTemplateItemModel>()

  private fetchCsvPromise = new StatefulPromise(this.handleFetchCsv.bind(this))

  fetchContactsPromise: StatefulPromise<
    Awaited<ReturnType<ContactsClient['fetch']>>,
    Parameters<ContactsClient['fetch']>
  >
  contactSharingSettingsPromise: StatefulPromise<
    Awaited<ReturnType<ContactsClient['settings']['fetch']>>,
    Parameters<ContactsClient['settings']['fetch']>
  >
  googleContactSettingsPromise: StatefulPromise<
    Awaited<ReturnType<ContactsClient['settings']['fetchGoogleSettings']>>,
    Parameters<ContactsClient['settings']['fetchGoogleSettings']>
  >

  constructor(
    private root: Service,
    private worker: MainWorker,
  ) {
    this.collection = new PersistedCollection({
      table: this.root.storage.table('contact'),
      classConstructor: (json: CodableContact) => new ContactModel(root.contact, json),
    })
    this.template = new PersistedCollection({
      table: this.root.storage.table('contactTemplateItem'),
      classConstructor: () => new ContactTemplateItemModel(root.contact),
    })
    this.googleContactSettings = new PersistedCollection({
      table: this.root.storage.table('contactGoogleSettings'),
      classConstructor: () => new GoogleContactSettingsModel(this),
    })

    this.settings = new PersistedCollection({
      table: this.root.storage.table('contactSettings'),
      classConstructor: (json: CodableSharedContactSettings) =>
        new SharedContactSettingsModel(json),
    })

    this.csvImportsV2 = new PersistedCollection<CsvImportV2Model, CsvImportV2Repository>({
      table: this.root.storage.table('csvImports'),
      classConstructor: (json: CodableCsvImportV2) => new CsvImportV2Model(json),
    })

    this.inMemoryCollection = new Collection<ContactModel>({
      maxSize: 10_000,
    })

    this.activeCsvImportsV2.bind(this.csvImportsV2)

    makeAutoObservable(this, {})

    makePersistable<this, 'lastContactsResyncId'>(this, 'ContactStore', {
      lastContactsResyncId: root.storage.sync(),
    })

    this.fetchContactsPromise = new StatefulPromise(
      (query: Parameters<ContactsClient['fetch']>[0]) =>
        this.root.transport.contacts.fetch(query),
    )
    this.contactSharingSettingsPromise = new StatefulPromise(() =>
      this.root.transport.contacts.settings.fetch(),
    )
    this.googleContactSettingsPromise = new StatefulPromise(() =>
      this.root.transport.contacts.settings.fetchGoogleSettings(),
    )

    this.handleIndexByPhoneNumber()

    this.disposeBag.add(this.subscribeToWebSocket())

    this.loadCsvImports()
  }
  /**
   * Don't use this getter, rely on asynchronous queries or other
   * ContactStore methods to achieve what you need.
   *
   * In case of doubts, please reach out to the team in #app-web.
   *
   * @deprecated
   */
  get deprecated_collection() {
    return this.contactCollection
  }

  private get contactCollection() {
    return this.inMemoryCollection
  }

  /**
   * Returns the total number of contacts.
   *
   * In the future, this count will be eventually consistent because
   * we'll only hold a subset of the contacts in the collection and
   * we'll either fetch the total count from the server or an asynchronous
   * query to the local database.
   */
  get totalContactCount() {
    return this.contactCollection.length
  }

  get sharedContactSettings(): SharedContactSettingsModel | null {
    return this.settings.list[0] ?? null
  }

  set sharedContactSettings(value: Partial<SharedContactSettingsModel> | null) {
    const settings = this.settings.list[0]
    const defaultSharingIds = value?.defaultSharingIds

    if (settings && defaultSharingIds) {
      settings.localUpdate({ defaultSharingIds })
    }
  }

  get defaultSharingIds(): readonly string[] {
    if (!this.sharedContactSettings?.defaultSharingIds) {
      return []
    }

    return this.root.workspace.filterDeletedGroupIds(
      this.sharedContactSettings.defaultSharingIds,
    )
  }

  get sortedTemplates() {
    return this.template.list.sort((i1, i2) => {
      if (i1.order == null) {
        return 1
      }
      if (i2.order == null) {
        return -1
      }
      return i1.order - i2.order
    })
  }

  /**
   * Returns the 10 first contacts from the Contact collection
   *
   * In the future, this getter could return a list of suggested contacts
   * based on the user's recent activity or other criteria.
   */
  get suggestedContacts() {
    return this.contactCollection.list.slice(0, 10)
  }

  onContactUpdate(callback: (contact: ContactModel) => void): Subscription {
    return this.contactUpdate$.subscribe(callback)
  }

  resyncGoogleContactSettings = (settings: RawGoogleContactSettings) => {
    return this.root.transport.contacts.settings.resync(settings)
  }

  onTemplateItemUpdate(
    callback: (contact: ContactTemplateItemModel) => void,
  ): Subscription {
    return this.contactTemplateItemUpdate$.subscribe(callback)
  }

  get(id: string) {
    void this.getById(id)

    return this.contactCollection.get(id)
  }

  getByNumber(number: string) {
    this.fetchContactByNumberFromApiBuffered(number)
    const contactsByNumber = this.byNumber.get(number)
    return contactsByNumber ? Array.from(contactsByNumber.values()) : []
  }

  /**
   * Fetches contact from API and updates the in-memory cache.
   * Resolves phone number -> contact ID via participants API,
   * then fetches the full contact data by ID to avoid overwriting
   * existing contacts with partial data.
   */
  async fetchContactsByNumbersFromApi(numbers: string[]): Promise<void> {
    const normalizedNumbersToFetch: string[] = []

    runInAction(() => {
      for (const number of numbers) {
        if (!isValidNumber(number)) {
          continue
        }

        const normalized = toE164(number)

        // Skip if already fetched or in-flight
        if (this.fetchedByNumberCache.has(normalized)) {
          continue
        }

        normalizedNumbersToFetch.push(normalized)

        this.fetchedByNumberCache.add(normalized)
      }
    })

    if (normalizedNumbersToFetch.length === 0) {
      return
    }

    try {
      const response =
        await this.root.transport.communication.conversations.getParticipantByPhoneNumber(
          normalizedNumbersToFetch,
        )

      if (response && Array.isArray(response)) {
        // Extract contact IDs from the participant response
        const contactIds = response
          .filter((item): item is ConversationParticipantCodable => {
            return item !== null && typeof item === 'object' && !Array.isArray(item)
          })
          .filter((item) => item.type === 'contact')
          .map((item) => item.id)
          .filter(Boolean)

        // Fetch full contact data by IDs (this properly stores them
        // in inMemoryCollection via load(), which triggers handleIndexByPhoneNumber
        // to update the byNumber index)
        if (contactIds.length > 0) {
          const contacts = await this.fetchContactsByIds([...new Set(contactIds)])
          if (contacts[0]) {
            this.contactUpdate$.next(contacts[0])
          }
        }
      }
    } catch (error) {
      // Clear the cache entry on failure so it can be retried
      runInAction(() => {
        for (const normalizedNumberToFetch of normalizedNumbersToFetch) {
          this.fetchedByNumberCache.delete(normalizedNumberToFetch)
        }
      })
      logError(new Error('Contact API call failed'), {
        originalError: error,
        phoneNumbers: numbers,
      })
    }
  }

  private fetchedAllContactsByNumberCache = new Set<string>()

  /**
   * Fetches ALL contacts matching the given phone numbers via GraphQL search.
   * The participants API only returns one contact per number and the integration
   * API only covers CRM-linked contacts, so neither finds regular duplicates.
   * GraphQL's contacts query with searchText finds every contact whose phone
   * numbers match, enabling proper duplicate detection.
   */
  async fetchAllContactsByPhoneNumbers(numbers: string[]): Promise<ContactModel[]> {
    const numbersToFetch: string[] = []

    for (const number of numbers) {
      if (!number.trim()) {
        continue
      }
      const normalized = toE164(number)
      if (this.fetchedAllContactsByNumberCache.has(normalized)) {
        continue
      }
      this.fetchedAllContactsByNumberCache.add(normalized)
      numbersToFetch.push(normalized)
    }

    if (numbersToFetch.length === 0) {
      return []
    }

    try {
      const graphqlUrl = new URL('/api/graphql', window.location.origin).toString()
      const allContactIds: string[] = []

      for (const number of numbersToFetch) {
        const response: {
          data?: { contacts?: { edges?: { node: { id: string } }[] } }
        } = await this.root.transport.client.post(graphqlUrl, {
          query: `query SearchContactsByPhone($searchText: String!) {
            contacts(first: 20, filter: { searchText: $searchText }) {
              edges { node { id } }
            }
          }`,
          variables: { searchText: number },
        })

        const edges = response?.data?.contacts?.edges
        if (edges) {
          allContactIds.push(...edges.map((e) => e.node.id))
        }
      }

      if (allContactIds.length === 0) {
        return []
      }

      return await this.fetchContactsByIds([...new Set(allContactIds)])
    } catch (error) {
      for (const n of numbersToFetch) {
        this.fetchedAllContactsByNumberCache.delete(n)
      }
      logError(new Error('Failed to fetch contacts by phone number via GraphQL'), {
        originalError: error,
        phoneNumbers: numbers,
      })
      return []
    }
  }

  getByNumberSorted(number: string) {
    // Check memory cache first
    const contactsByNumber = this.byNumber.get(number)
    const memoryContacts = contactsByNumber ? Array.from(contactsByNumber.values()) : []

    // If no contacts in memory, fetch from API
    if (memoryContacts.length === 0) {
      void this.fetchContactsByNumbersFromApi([number])
    }

    // Return what we have now (empty or cached)
    // API call will update reactively when it completes
    return memoryContacts.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0))
  }

  loadAllIfNecessary = () => {
    // With GraphQL contacts, contacts are fetched on demand rather than
    // bulk-loaded. `loaded` is still toggled so consumers that gate on it
    // (e.g. InboxUiStore filtering) proceed after the initial call.
    this.loaded = true
  }

  async fetchPaginatedContacts(
    {
      lastId,
      limit,
      dir,
    }: {
      lastId?: string
      limit?: number
      dir?: 'desc' | 'asc'
    },
    transaction: TransactionConfiguration = {},
  ) {
    const result = await this.root.transport.contacts.fetch(
      {
        lastId,
        limit,
        dir,
      },
      transaction,
    )

    const contacts = await this.load(result.result)

    return {
      contacts,
      pageInfo: result.pageInfo,
    }
  }

  /**
   * Tries to get a contact from memory first,
   * if not found, makes an API call to fetch it.
   */
  getById = async (id: string): Promise<ContactModel | null> => {
    return this.contactByIdLoader.load(id)
  }

  /**
   * Tries to fetch the full contact data for a list of contact ids.
   * If the contact is not found in memory,
   * it will be fetched from the server.
   */
  getByIds = async (ids: readonly string[]): Promise<ContactModel[]> => {
    const existingContacts: ContactModel[] = []
    const idsToFetch: string[] = []

    for (const id of ids) {
      const existingContact = this.contactCollection.get(id)
      if (existingContact !== null) {
        existingContacts.push(existingContact)
      } else {
        idsToFetch.push(id)
      }
    }

    if (idsToFetch.length === 0) {
      return existingContacts
    }

    const fetchResponses = await Promise.all(
      chunk(idsToFetch, CONTACT_BULK_GET_MAX_IDS).map((batchIds) =>
        this.root.transport.contacts.fetchByIds(batchIds),
      ),
    )
    const newContactModels = await this.load(
      fetchResponses.flatMap((response) => response.contacts),
    )

    return [...existingContacts, ...newContactModels]
  }

  /**
   * Tries to fetch the full contact data for a list of contact ids.
   * If the contact is not found in memory or in the local database,
   * it will be fetched from the server.
   */
  fetchContactsByIds = (ids: string[]): Promise<ContactModel[]> => {
    return this.getByIds(ids)
  }

  fetchContactByNumberFromApiBuffered = buffer<string>(
    (objects) => void this.fetchContactsByNumbersFromApi(objects),
    { duration: 1000, maxSize: 100 },
  )

  /**
   * Drops a number from the fetch caches so the next read requests it again.
   * The caches are keyed by E.164, while the callers hold raw item values.
   */
  private forgetFetchedNumber(phoneNumber: string) {
    if (!isValidNumber(phoneNumber)) {
      return
    }

    const normalized = toE164(phoneNumber)
    this.fetchedByNumberCache.delete(normalized)
    this.fetchedAllContactsByNumberCache.delete(normalized)
  }

  googleSync = (token: string, redirectUri = 'postmessage'): Promise<any> => {
    return this.root.transport.contacts.googleSync(token, redirectUri)
  }

  updateContactItem = async (contact: ContactModel, contactItem: ContactItemModel) => {
    this.contactCollection.put(contact)
    // If the contact was never persisted (user added items without saving name),
    // create it on the server first so the item PUT succeeds.
    if (contact.local) {
      await this.update(contact)
    }
    return this.root.transport.contacts.updateContactItem(
      contact.id,
      contactItem.toJSON(),
    )
  }

  update = (contact: ContactModel) => {
    // If there is a suggestion associated with the contact and the contact is local,
    // we want to accept the updated suggestion instead.
    const contactSuggestion = this.root.contactSuggestion.getByContact(contact)
    if (contactSuggestion && contact.local) {
      const suggestedFields = this.root.contactSuggestion.getMergedSuggestedFields(
        contact,
        contactSuggestion,
      )

      // We want to track updates made to "suggested" contacts
      this.root.analytics.workspace.aiContactSuggestionEdited(
        Object.keys(contact.serialize()),
        Object.keys(omitNullish(suggestedFields)),
      )

      return this.root.contactSuggestion.update(
        contactSuggestion,
        'accepted',
        suggestedFields,
      )
    }

    contact.localUpdate({ local: false })
    this.contactCollection.put(contact)

    this.root.analytics.workspace.contactUpdated()

    return this.root.transport.contacts.update(contact.toJSON())
  }

  merge = async (contact: ContactModel, withContacts: ContactModel[]) => {
    // TODO: offline support
    const result = await this.root.transport.contacts.merge(
      contact.id,
      withContacts.map((c) => c.id),
    )
    const mergedIds = withContacts.map((c) => c.id)
    // If the server created a new contact, the destination was also deleted
    if (result.newContactId !== contact.id) {
      mergedIds.push(contact.id)
    }
    for (const id of mergedIds) {
      this.recentlyMergedIds.add(id)
    }
    this.contactCollection.deleteBulk(mergedIds)
    return result
  }

  delete = async (contact: ContactModel) => {
    this.contactCollection.delete(contact)
    if (!contact.local) {
      return this.root.transport.contacts.delete(contact.id)
    }
  }

  deleteBySource = (source: string, sourceName: string) => {
    this.loadAllIfNecessary()
    const ids = this.contactCollection.list
      .filter((c) => c.source === source && c.sourceName === sourceName)
      .map((c) => c.id)
    return this.deleteBulk(ids)
  }

  deleteLegacyCSVImportsByName = (name: string) => {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    this.csvImports = this.csvImports.filter((i) => i.name !== name)
  }

  /**
   * Deletes all contacts that were imported from the public API.
   *
   * We do this as a temporary measure to clean up API contacts on reload
   * because we don't have a mechanism to remove deleted contacts from the
   * public API.
   *
   * Called during app load, will timeout after 15 seconds to prevent users
   * from being stuck on a loading screen in very large workspaces (in terms of
   * contacts amount)
   */
  async deleteFromPublicApi() {
    try {
      const timeoutPromise = new Promise((resolve, reject) => {
        setTimeout(() => {
          reject(new Error('ContactStore.deleteFromPublicApi timeout'))
        }, 15000)
      })

      await Promise.race([
        this.collection
          .performQuery((repo) => repo.deleteFromPublicApi())
          .catch(logError),
        timeoutPromise,
      ])
    } catch (e) {
      logError(e)
    }
  }

  deleteAll = () => {
    const ids = this.contactCollection.list.map((c) => c.id)
    return this.deleteBulk(ids)
  }

  deleteBulk = (ids: string[]) => {
    if (!ids || ids.length === 0) {
      return Promise.resolve()
    }
    this.contactCollection.deleteBulk(ids)
    return Promise.all(
      chunk(ids, 500).map((ids) => this.root.transport.contacts.deleteBulk(ids)),
    )
  }

  shareBulk = async (
    contactIds: string[],
    shareIds: string[],
    progress: (progress: number) => void,
  ) => {
    progress(0)
    const batches = chunk(contactIds, 250)
    for (let i = 0; i < batches.length; i++) {
      const ids = batches[i]
      // @ts-expect-error unchecked index access
      await this.root.transport.contacts.shareBulk(ids, shareIds)
      progress((i + 1) / (batches.length - 1))
    }
  }

  putNote = (note: NoteModel) => {
    this.contactCollection.put(note.contact)
    return this.root.transport.contacts.note.put(note.contact.id, note.toJSON())
  }

  deleteNote = (note: NoteModel) => {
    this.contactCollection.put(note.contact)
    return this.root.transport.contacts.note.deleteNote(note.contact.id, note.id)
  }

  fetchTemplates = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.template.performQuery((repo) => repo.all())
    return this.root.transport.contacts.template
      .fetch()
      .then(action((res) => this.template.load(res, { deleteOthers: true })))
  }

  updateTemplate = (template: ContactTemplateItemModel) => {
    template.localUpdate({ local: false })
    this.template.put(template)
    return this.root.transport.contacts.template
      .put(template.serialize())
      .then(this.template.load)
  }

  deleteTemplate = async (template: ContactTemplateItemModel) => {
    this.template.delete(template)
    if (!template.local) {
      return this.root.transport.contacts.template.delete(template.serialize())
    }
  }

  reorderTemplates = (from: number, to: number) => {
    const sorted = [...this.sortedTemplates]
    const item = sorted[from]
    const reordered = insertAtIndex(removeAtIndex(sorted, from), item, to)
      .map((t, order) => {
        t?.localUpdate({ order })
        return t
      })
      .filter((t): t is ContactTemplateItemModel => t !== undefined)
    void this.root.transport.contacts.template.post(reordered.map((t) => t.serialize()))
  }

  fetchGoogleContactSettings = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.googleContactSettings.performQuery((repo) => repo.all())
    return this.googleContactSettingsPromise
      .run()
      .then((res) => this.googleContactSettings.load(res, { deleteOthers: true }))
  }

  fetchSettings = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.settings.performQuery((repo) => repo.all())
    return this.contactSharingSettingsPromise.run().then((res) => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
      this.settings.load(res, { deleteOthers: true })
    })
  }

  saveLocalContact(contact: ContactModel) {
    this.root.analytics.workspace.contactCreated()
    this.contactCollection.put(contact)
  }

  private handleFetchCsv() {
    return this.root.transport.contacts.csv.getAllImports()
  }

  get fetchCsvStatus() {
    return this.fetchCsvPromise.status
  }

  async loadCsvImportsV2() {
    if (this.fetchCsvStatus === 'idle') {
      startDurationVital({
        name: 'contact-load-csv-imports',
        options: {},
      })
      const response = await this.fetchCsvPromise.run()
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
      this.csvImportsV2.load(response).then(() => {
        stopDurationVital('contact-load-csv-imports')
      })
    }
  }

  loadCsvImports() {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.worker.service.contact.getUniqueSources('csv').then(
      action((data) => {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        this.csvImports = data.map((item) => ({
          name: item.sourceName,
          userId: item.userId,
        }))
      }),
    )
  }

  updateSettings = (
    settings: Pick<CodableSharedContactSettings, 'defaultSharingIds'>,
  ) => {
    return this.root.transport.contacts.settings.put(settings)
  }

  deleteSettings = (id: string) => {
    this.googleContactSettings.delete(id)
    return this.root.transport.contacts.settings.delete(id)
  }
  addNoteReaction(
    contactId: string,
    noteId: string,
    reaction: CodableContactNoteReaction,
  ) {
    return this.root.transport.contacts.note.addReaction(contactId, noteId, reaction)
  }

  deleteNoteReaction(
    contactId: string,
    noteId: string,
    reaction: CodableContactNoteReaction,
  ) {
    return this.root.transport.contacts.note.deleteReaction(
      contactId,
      noteId,
      reaction.id,
    )
  }

  /**
   * Calculates the total number of unique members that are contained
   * in the list of entity ids.
   *
   * @param entityIds string[] - List of user, organization, and group ids
   */
  getUniqueMemberCountInEntities(entityIds: string[]) {
    const org = this.root.organization.current
    if (org && entityIds.includes(org.id)) {
      return this.root.member.collection.length
    }

    const userMembersIds = entityIds.filter((id) => id.startsWith('US'))
    const groupAndPhoneNumberMembersIds = entityIds
      .filter((id) => id.startsWith('GR'))
      .reduce((result, groupId) => {
        const phoneNumberOrGroup =
          this.root.workspace.activeGroups.find((group) => group.id === groupId) ??
          this.root.phoneNumber.collection.list.find((ph) => ph.groupId === groupId)

        const membersIds: string[] =
          phoneNumberOrGroup?.members.map((member: MemberModel | GroupMembership) => {
            if (member instanceof MemberModel) {
              return member.id
            }

            return member.userId
          }) ?? []
        return [...result, ...membersIds]
      }, [] as string[])
    return new Set([...userMembersIds, ...groupAndPhoneNumberMembersIds]).size
  }

  submitCsvImportMetadata = (
    metadata: Parameters<typeof this.root.transport.contacts.csv.import>[0],
  ) => {
    return this.root.transport.contacts.csv.import(metadata)
  }

  updateCsvImportStatus = (
    id: Parameters<typeof this.root.transport.contacts.csv.importStatus>[0],
    jobMeta: Parameters<typeof this.root.transport.contacts.csv.importStatus>[1],
  ) => {
    return this.root.transport.contacts.csv.importStatus(id, jobMeta)
  }

  submitCsvFile = (file: File, url: string) => {
    return this.root.transport.contacts.csv.submitFile(file, url)
  }

  /**
   * Deletes a CSV Import entry from the collection,
   * as well as all the contacts associated with it.
   *
   * @param id string - csv import identifier
   */
  deleteCsvImport = (id: string) => {
    const csvImport = this.csvImportsV2.get(id)

    if (csvImport) {
      csvImport.deletedAt = new Date().toISOString()
      this.csvImportsV2.put(csvImport)
    }

    return this.root.transport.contacts.csv.deleteImport(id)
  }

  submitCsvImport = async (
    file: File,
    metadata: Parameters<typeof this.submitCsvImportMetadata>[0],
    customFields: ContactTemplateItemModel[],
  ) => {
    const [response] = await Promise.all([
      // submit csv job metadata
      this.submitCsvImportMetadata(metadata),
      // Create template items if needed
      ...customFields.map((field) => this.updateTemplate(field)),
    ])

    if (!response || !file) {
      return
    }

    await this.submitCsvFile(file, response.s3SignedUrl)

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.updateCsvImportStatus(response.id, { status: 'uploaded', retries: 0 })
  }

  prepareResync(id: string) {
    if (this.lastContactsResyncId === null) {
      // First time we call this function will be after logging in
      // so we need to set the lastContactsResyncId to the first resync id we get
      this.lastContactsResyncId = id
      return
    }

    if (id <= this.lastContactsResyncId) {
      return
    }

    this.lastContactsResyncId = id
  }

  /**
   * Clears the in-memory contacts so subsequent reads refetch from GraphQL.
   */
  forceReloadContacts() {
    this.contactCollection.clear()
  }

  private subscribeToWebSocket() {
    return this.root.transport.onNotificationData.subscribe((data) => {
      switch (data.type) {
        case 'reaction-update':
          if (isCodableContactNoteReaction(data.reaction)) {
            return this.handleContactNoteReactionUpdate(data.reaction)
          }
          break
        case 'reaction-delete':
          if (isCodableContactNoteReaction(data.reaction)) {
            return this.handleContactNoteReactionDelete(data.reaction)
          }
          break
        case 'contact-update':
        case 'contact-note-update':
        case 'contact-note-delete': {
          this.updateCsvImportSourcesIfNeeded(data.contact)

          // We need to update only those contacts that are already in memory
          // and skip the rest.
          const contact = this.inMemoryCollection.get(data.contact.id)
          if (contact) {
            contact.deserialize(data.contact)
            this.contactCollection.put(contact)
            this.contactUpdate$.next(contact)
            break
          }

          // Contacts we don't hold yet are only worth loading when one of their
          // numbers belongs to a participant on screen: that participant resolves
          // its contact by number, and a by-number miss is memoized for the
          // session, so this event is its only chance to stop being anonymous.
          if (this.hasParticipantForContact(data.contact)) {
            // `load` settles synchronously, so the contact is in the collection
            // before the microtask runs.
            void this.load([data.contact]).then(([loaded]) => {
              if (loaded) {
                this.contactUpdate$.next(loaded)
              }
            })
          }
          break
        }
        case 'contact-delete':
          this.recentlyMergedIds.delete(data.contact.id)
          return this.contactCollection.delete(data.contact.id)
        case 'integrations-disconnected': {
          this.forceReloadContacts()
          return
        }
        case 'template-update': {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
          this.template.load(data.template)
          const template = this.template.get(data.template.id)
          if (template) {
            this.contactTemplateItemUpdate$.next(template)
          }
          return
        }
        case 'template-delete':
          return this.template.delete(data.template.id)
        case 'google-people-sync-progress':
          return this.handleGooglePeopleSyncProgressNotification(data)
        case 'contact-settings-update':
          return this.handleContactSettingsUpdate(data.settings)
        case 'contact-settings-delete':
          return this.handleContactSettingsDelete(data.settings)
        case 'csv-import-processed':
          return this.handleCsvImportProcessed(data.data)
      }
    })
  }

  private handleCsvImportProcessed(value: CodableCsvImportV2) {
    this.csvImportsV2.put(new CsvImportV2Model(value))
  }

  private handleContactNoteReactionUpdate(value: CodableContactNoteReaction) {
    const contact = value.contactId ? this.contactCollection.get(value.contactId) : null
    const note = contact?.notes.find((note) => note.id === value.noteId)

    if (note) {
      const reaction = note.reactions.find((reaction) => reaction.id === value.id)

      if (reaction) {
        reaction.deserialize(value)
      } else {
        note.reactions.push(new ContactNoteReactionModel(value))
      }
    }
  }

  private handleContactNoteReactionDelete(value: CodableContactNoteReaction) {
    const contact = value.contactId ? this.contactCollection.get(value.contactId) : null
    const note = contact?.notes.find((note) => note.id === value.noteId)

    if (!note) {
      return
    }

    const reaction = note.reactions.find((reaction) => reaction.id === value.id)
    if (reaction) {
      void note.deleteReaction(reaction)
    }
  }

  private updateCsvImportSourcesIfNeeded(contact: CodableContact) {
    if (
      contact.source === 'csv' &&
      contact.sourceName &&
      contact.userId &&
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      !this.csvImports.some((source) => source.name === contact.sourceName)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      this.csvImports.push({
        name: contact.sourceName,
        userId: contact.userId,
      })
    }
  }

  private handleContactSettingsUpdate(settings: CodableSharedContactSettings) {
    const item = this.settings.find((item) => item.id === settings.id)
    if (item) {
      item.deserialize(settings)
      this.settings.put(item)
    }
  }

  private handleContactSettingsDelete(settings: CodableSharedContactSettings) {
    this.settings.delete(settings.id)
  }

  private handleGooglePeopleSyncProgressNotification(
    msg: GooglePeopleSyncProgressNotification,
  ) {
    const settings = this.googleContactSettings.list.find(
      (s) => s.source === msg.status.source,
    )
    if (!settings) {
      return
    }
    settings.localUpdate({ resyncStatus: msg.status.state })
  }

  /**
   * Whether any of the contact's phone numbers belongs to a participant we
   * already track. Matches raw values, like the `byNumber` index does.
   */
  private hasParticipantForContact(contact: CodableContact) {
    return contact.items.some(
      (item) =>
        item.type === 'phone-number' &&
        isNonNull(item.value) &&
        this.root.participant.get(String(item.value)) !== null,
    )
  }

  /**
   * As contacts are added/updated/deleted from the collection, this
   * function keeps map of phone numbers to contacts for fast lookup
   */
  private handleIndexByPhoneNumber() {
    const handleObserve = (event: CollectionChange<ContactModel>) => {
      if (event.type === 'put') {
        event.objects.forEach((contact) => {
          this.contactNumbers[contact.id] ??= []
          const numbers = this.contactNumbers[contact.id]
          // @ts-expect-error unchecked index access
          numbers.forEach((number) => {
            const contactsByNumber = this.byNumber.get(number)
            if (contactsByNumber?.has(contact.id)) {
              contactsByNumber.delete(contact.id)
              // Replace with new Map so the ObservableMap detects the value change
              this.byNumber.set(number, new Map(contactsByNumber))
            }
          })
          contact.phoneNumbers.forEach((item) => {
            const number = String(item.value)

            if (!this.byNumber.has(number)) {
              this.byNumber.set(number, new Map())
            }

            const contactsByNumber = this.byNumber.get(number)
            if (contactsByNumber && !contactsByNumber.has(contact.id)) {
              contactsByNumber.set(contact.id, contact)
              // Replace with new Map so the ObservableMap detects the value change
              this.byNumber.set(number, new Map(contactsByNumber))
            }
          })

          this.contactNumbers[contact.id] = contact.phoneNumbers.map((i) =>
            String(i.value),
          )
        })
      } else if (event.type == 'delete') {
        event.objects.forEach((object) => {
          const numbers = this.contactNumbers[object.id]
          numbers?.forEach((number) => {
            const contactsByNumber = this.byNumber.get(number)
            if (contactsByNumber?.has(object.id)) {
              contactsByNumber.delete(object.id)
              this.byNumber.set(number, new Map(contactsByNumber))
            }

            // Deletes include LRU evictions, which we don't control. Leaving an
            // uncovered number marked as fetched would keep `getByNumber`
            // answering empty for the rest of the session.
            if (this.byNumber.get(number)?.size === 0) {
              this.forgetFetchedNumber(number)
            }
          })
          remove(this.contactNumbers, object.id)
        })
      }
    }

    this.disposeBag.add(
      this.collection.observe(action(handleObserve)),
      this.inMemoryCollection.observe(action(handleObserve)),
    )
  }

  load(contacts: CodableContact[]): Promise<ContactModel[]> {
    if (contacts.length === 0) {
      return Promise.resolve([])
    }

    this.contactCollection.deleteBulk(
      contacts.filter((c) => c.deletedAt).map((c) => c.id),
    )

    const newContacts = contacts
      .filter((c) => !c.deletedAt && !this.recentlyMergedIds.has(c.id))
      .map((c) => new ContactModel(this, c))
    this.inMemoryCollection.putBulk(newContacts)
    return Promise.resolve(newContacts)
  }

  tearDown() {
    this.disposeBag.dispose()
  }
}
